import { useRef, useState, useCallback } from 'react';
import { HighlightedResume } from '../components/HighlightedResume';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useApplication, useReprocessApplication, useArchiveApplication, useForceReview } from '../hooks/useApi';
import { api } from '../api/client';
import { useToast } from '../contexts/ToastContext';
import { StatusBadge } from '../components/StatusBadge';
import { ResumeViewer } from '../components/ResumeViewer';
import { CandidateCard } from '../components/CandidateCard';
import { JobCard } from '../components/JobCard';
import { ReviewCard } from '../components/ReviewCard';
import { ReviewVersionHistory } from '../components/ReviewVersionHistory';
import { SyncStatusCard } from '../components/SyncStatusCard';
import { FeedbackPanel } from '../components/FeedbackPanel';
import { PIIAuditPanel } from '../components/PIIAuditPanel';
import { ChatDrawer } from '../components/ChatDrawer';
import { EeoExclusionPill } from '../components/EeoExclusionPill';
import { ApplicationActionBar } from '../components/ApplicationActionBar';
import { ScreeningPolicyVerdicts } from '../components/ScreeningPolicyVerdicts';
import { Button, SkeletonDetail } from '../components/ui';
import {
  ArrowLeft,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  AlertCircle,
  UserCheck,
  PlayCircle,
  Shield,
  ShieldOff,
  ShieldQuestion,
  XCircle,
  ArrowRight,
  Clock,
  MessageSquare,
  MessageCircle,
  Activity,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import type { ConsentDecisions, StageTransition } from '../types';

export default function ApplicationDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const applicationId = parseInt(id || '0', 10);

  const { data: application, isLoading, error, refetch } = useApplication(applicationId);
  const reprocessMutation = useReprocessApplication();
  const archiveMutation = useArchiveApplication();
  const forceReviewMutation = useForceReview();
  const { showToast } = useToast();

  const [isScreeningExpanded, setIsScreeningExpanded] = useState(false);
  const [feedbackFormOpen, setFeedbackFormOpen] = useState(false);
  const [showHighlights, setShowHighlights] = useState(true);
  const [isFeedbackSectionOpen, setIsFeedbackSectionOpen] = useState(true);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const feedbackRef = useRef<HTMLDivElement>(null);

  // US-139: Interviewer Notes (HireLoop messages) — loaded on demand
  const [isNotesExpanded, setIsNotesExpanded] = useState(false);
  const [notesLoaded, setNotesLoaded] = useState(false);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesData, setNotesData] = useState<{ messages: { id: string; authorId: string; authorName: string; body: string; createdAt: string }[]; scopeError: boolean } | null>(null);

  // US-139: Load HireLoop messages on first expand, then toggle
  const handleLoadNotes = useCallback(async () => {
    if (notesLoaded) {
      setIsNotesExpanded((prev) => !prev);
      return;
    }
    setIsNotesExpanded(true);
    setNotesLoading(true);
    try {
      const result = await api.getApplicationMessages(applicationId);
      setNotesData(result);
      setNotesLoaded(true);
    } catch (err) {
      console.error('Failed to load interviewer notes:', err);
      showToast('Failed to load interviewer notes', 'error');
    } finally {
      setNotesLoading(false);
    }
  }, [notesLoaded, applicationId, showToast]);

  const handleForceReview = async () => {
    if (
      window.confirm(
        'Force an AI review for this candidate? This will override the human-review skip and queue the candidate for AI analysis.'
      )
    ) {
      try {
        await forceReviewMutation.mutateAsync(applicationId);
        showToast('AI review queued (force override)', 'success');
        refetch();
      } catch (err) {
        console.error('Force review failed:', err);
        showToast('Failed to force AI review', 'error');
      }
    }
  };

  const handleReprocess = async () => {
    showToast('Review requested', 'success');
    try {
      await reprocessMutation.mutateAsync({
        id: applicationId,
        reason: 'Manual reprocess from detail page',
      });
      refetch();
    } catch (err) {
      console.error('Reprocess failed:', err);
    }
  };

  const handleArchive = async () => {
    if (
      window.confirm(
        'Are you sure you want to archive this application? It will be set to stub status.'
      )
    ) {
      try {
        await archiveMutation.mutateAsync(applicationId);
        showToast('Application archived successfully', 'success');
        navigate('/applications');
      } catch (err) {
        console.error('Archive failed:', err);
        showToast('Failed to archive application', 'error');
      }
    }
  };

  if (error) {
    return (
      <div className="space-y-6">
        <button
          onClick={() => navigate('/applications')}
          className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Applications
        </button>
        <div className="card dark:bg-gray-800 dark:border-gray-700 p-6">
          <div className="text-center">
            <AlertCircle className="mx-auto h-12 w-12 text-red-500" />
            <p className="mt-4 text-red-600 dark:text-red-400">
              Error loading application: {error.message}
            </p>
            <Button onClick={() => refetch()} variant="primary" className="mt-4">
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading || !application) {
    return <SkeletonDetail showSidebar={true} sections={4} />;
  }

  const screeningAnswers = application.screeningAnswers
    ? Object.entries(application.screeningAnswers)
    : [];

  // Most recent non-archived review (falls back to latest of any) — mirrors the
  // API's projection used for screening_policy_verdicts.
  const allReviews = application.reviews ?? [];
  const latestReview =
    [...allReviews].reverse().find((r) => !r.archivedAt) ??
    allReviews[allReviews.length - 1] ??
    null;

  // Determine if we have review version history to display
  const hasMultipleVersions = application.reviews.length > 1 ||
    application.reviews.some(r => (r.version ?? 1) > 1 || r.archivedAt !== null);

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <button
        onClick={() => navigate('/applications')}
        className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Applications
      </button>

      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-sm text-stone-500 dark:text-stone-400">
        <Link to={`/jobs/${application.jobId}`} className="hover:text-stone-700 dark:hover:text-stone-200">
          {application.job?.title ?? 'Job'}
        </Link>
        <span>/</span>
        <span>{(application.srStage ?? '').replace('_', ' ')}</span>
        <span>/</span>
        <span className="text-stone-800 dark:text-stone-100 font-medium">
          {application.candidateFirstName} {application.candidateLastName}
        </span>
      </nav>

      {/* US-231: Sticky action bar */}
      <ApplicationActionBar
        applicationId={application.id}
        srApplicationId={application.srApplicationId ?? null}
        srStage={application.srStage ?? null}
        overallScore={latestReview?.overallScore ?? null}
        summary={latestReview?.summary ?? null}
        onChat={() => setIsChatOpen(true)}
        onRerun={handleForceReview}
        onArchive={handleArchive}
      />

      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {application.candidate
                  ? `${application.candidate.firstName} ${application.candidate.lastName}`
                  : 'Candidate Name Loading...'}
              </h1>
              <StatusBadge status={application.status} size="md" />
              {application.humanReviewDetected && application.skipReason === 'human_reviewed' && (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 dark:bg-amber-900/30 px-2.5 py-1 text-xs font-medium text-amber-700 dark:text-amber-400">
                  <UserCheck className="h-3.5 w-3.5" />
                  Human reviewed — AI review skipped
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {application.job?.title || 'Unknown Job'}
            </p>
            {application.srStage && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Stage: <span className="font-medium">{application.srStage}</span>
              </p>
            )}
            {application.lastActivityAt && (
              <p
                className="text-sm text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-1"
                title="Last time a review or update was received from SmartRecruiters"
              >
                <Activity className="h-3.5 w-3.5" />
                Last SR Activity:{' '}
                <span className="font-medium">
                  {formatDistanceToNow(new Date(application.lastActivityAt), { addSuffix: true })}
                </span>
              </p>
            )}
            {application.srStage === 'REJECTED' && application.rejectionReasonId && (
              <p className="text-sm text-red-600 dark:text-red-400 mt-1 flex items-center gap-1">
                <XCircle className="h-3.5 w-3.5" />
                Rejection Reason: <span className="font-medium">{application.rejectionReasonId}</span>
              </p>
            )}
            {application.consentDecisions && (
              <div className="flex items-center gap-2 mt-2">
                <span className="text-xs text-gray-500 dark:text-gray-400">GDPR:</span>
                {(['SMART_RECRUIT', 'SMART_CRM', 'SINGLE'] as const).map((key) => {
                  const decision = (application.consentDecisions as ConsentDecisions)?.[key];
                  if (!decision) {
                    return (
                      <span key={key} className="inline-flex items-center gap-1 rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5 text-xs text-gray-500 dark:text-gray-400">
                        <ShieldQuestion className="h-3 w-3" />
                        {key.replace('_', ' ')}
                      </span>
                    );
                  }
                  const consented = decision.status === 'CONSENTED';
                  return (
                    <span
                      key={key}
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
                        consented
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                          : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                      }`}
                    >
                      {consented ? <Shield className="h-3 w-3" /> : <ShieldOff className="h-3 w-3" />}
                      {key.replace('_', ' ')}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
          {application.humanReviewDetected && application.skipReason === 'human_reviewed' && (
            <Button
              variant="secondary"
              onClick={handleForceReview}
              loading={forceReviewMutation.isPending}
              leftIcon={<PlayCircle className="h-4 w-4" />}
              className="text-amber-600 dark:text-amber-400 border-amber-300 dark:border-amber-700 hover:bg-amber-50 dark:hover:bg-amber-950"
            >
              Override and Re-review
            </Button>
          )}
        </div>

        {/* Error Message */}
        {application.errorMessage && (
          <div className="mt-4 p-4 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-900 dark:text-red-200">Error Details</p>
                <p className="text-sm text-red-700 dark:text-red-300 mt-1">{application.errorMessage}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Candidate Card */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">Candidate</h2>
            <Link to={`/people/${application.candidate.id}`}>
              <CandidateCard
                candidate={application.candidate}
                className="hover:border-gray-300 dark:hover:border-gray-600 transition-colors cursor-pointer"
              />
            </Link>
          </div>

          {/* Job Card */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">Job</h2>
            <Link to={`/jobs/${application.job.id}`}>
              <JobCard
                job={application.job}
                className="hover:border-gray-300 dark:hover:border-gray-600 transition-colors cursor-pointer"
              />
            </Link>
          </div>

          {/* Screening Answers + Policy Verdicts + EEOC audit pill */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            {/* Policy verdicts (from latest review) — renders null if no verdicts */}
            {latestReview?.screeningPolicyVerdicts && latestReview.screeningPolicyVerdicts.length > 0 && (
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <ScreeningPolicyVerdicts verdicts={latestReview.screeningPolicyVerdicts} />
              </div>
            )}

            {screeningAnswers.length > 0 && (
              <>
                <button
                  onClick={() => setIsScreeningExpanded(!isScreeningExpanded)}
                  className="w-full flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
                >
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Screening Answers ({screeningAnswers.length})
                  </h2>
                  {isScreeningExpanded ? (
                    <ChevronUp className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  )}
                </button>

                {isScreeningExpanded && (
                  <div className="p-4 space-y-4">
                    {screeningAnswers.map(([question, answer], index) => (
                      <div key={index} className="pb-4 border-b border-gray-100 dark:border-gray-700 last:border-0 last:pb-0">
                        <dt className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {question}
                        </dt>
                        <dd className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">
                          {answer}
                        </dd>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* EEOC audit pill — count-only, never displays question labels.
                Wrapper auto-hides when pill renders null (count=0). */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 first:border-t-0 empty:hidden">
              <EeoExclusionPill applicationId={applicationId} />
            </div>
          </div>

          {/* Two-column: Resume (with Arbie highlights) + Timeline */}
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Left: Resume with highlights */}
            <div className="bg-white dark:bg-gray-800 border border-stone-200 dark:border-gray-700 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-sm text-stone-800 dark:text-stone-100">Resume</h3>
                <button
                  aria-label={`Toggle Arbie highlights ${showHighlights ? 'on' : 'off'}`}
                  onClick={() => setShowHighlights(h => !h)}
                  className={`text-xs px-2 py-1 rounded font-medium transition-colors ${showHighlights ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400' : 'bg-stone-100 dark:bg-gray-700 text-stone-500 dark:text-stone-400'}`}
                >
                  Arbie highlights {showHighlights ? 'on' : 'off'}
                </button>
              </div>
              {application.candidate?.resumeText ? (
                <HighlightedResume
                  resumeText={application.candidate.resumeText}
                  highlights={(latestReview?.strengths ?? '').split(/[,\n]+/).map(s => s.trim()).filter(Boolean)}
                  showHighlights={showHighlights}
                />
              ) : (
                <p className="text-sm text-stone-400 dark:text-stone-500 italic">No resume text available.</p>
              )}
            </div>

            {/* Right: Application Timeline */}
            <div className="bg-white dark:bg-gray-800 border border-stone-200 dark:border-gray-700 rounded-xl p-4">
              <h3 className="font-semibold text-sm text-stone-800 dark:text-stone-100 mb-3">Application Timeline</h3>
              {application.transitionHistory && (application.transitionHistory as StageTransition[]).length > 0 ? (
                <ol className="relative border-l border-stone-200 dark:border-gray-700 ml-2">
                  {(application.transitionHistory as StageTransition[]).map((t, i) => (
                    <li key={i} className="mb-4 ml-4">
                      <div className="absolute -left-1.5 mt-1.5 w-3 h-3 rounded-full border-2 border-white dark:border-gray-900 bg-sky-500" />
                      <p className="text-sm font-medium text-stone-800 dark:text-stone-100">{t.to}</p>
                      <p className="text-xs text-stone-400 dark:text-stone-500">{new Date(t.timestamp).toLocaleDateString()}</p>
                      {t.reason && <p className="text-xs text-amber-600 dark:text-amber-400 italic">{t.reason}</p>}
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="text-sm text-stone-400 dark:text-stone-500 italic">No stage history available.</p>
              )}
            </div>
          </div>

          {/* Resume Viewer */}
          <ResumeViewer
            resumeText={application.candidate.resumeText}
            applicationId={applicationId}
            candidateId={application.candidate.id}
            onResumeUpdated={() => refetch()}
          />

          {/* PII Redaction Audit */}
          <PIIAuditPanel
            originalText={application.candidate.resumeText}
            sanitizedText={application.candidate.sanitizedResumeText}
            applicationId={applicationId}
            candidateName={application.candidate ? `${application.candidate.firstName} ${application.candidate.lastName}` : undefined}
            processedAt={application.candidate.resumeProcessedAt}
          />

          {/* Reviews */}
          <div>
            {/* Last reviewed meta */}
            {application.reviews.length > 0 && (
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                Last reviewed {formatDistanceToNow(new Date(application.reviews[0].createdAt), { addSuffix: true })}
                {' '}by {application.reviews[0].reviewer || 'AI'}
                {application.reviews[0].modelTier ? ` (${application.reviews[0].modelTier})` : ''}
              </div>
            )}
            {application.reviews.length === 0 ? (
              <>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  AI Reviews (0)
                </h2>
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-8 text-center">
                  <p className="text-gray-500 dark:text-gray-400">No reviews yet</p>
                  {application.status !== 'ai_complete' && application.status !== 'synced' && (
                    <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                      Review will be generated when AI processing is complete
                    </p>
                  )}
                </div>
              </>
            ) : hasMultipleVersions ? (
              <ReviewVersionHistory
                reviews={application.reviews}
                screeningAnswers={application.screeningAnswers}
              />
            ) : (
              <>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  AI Review
                </h2>
                <ReviewCard
                  review={application.reviews[0]}
                  showFullDetails={true}
                  screeningAnswers={application.screeningAnswers}
                />
              </>
            )}
          </div>
        </div>

        {/* Right Column - Timeline and Sync Status */}
        <div className="lg:col-span-1 space-y-6">
          {/* Sync Status Card */}
          <SyncStatusCard
            applicationId={application.id}
            reviewId={application.reviews[0]?.id}
            syncInfo={application.syncInfo}
            srReviewId={application.reviews[0]?.srReviewId}
          />

          {/* Stage Transition History (US-086) */}
          {application.transitionHistory && application.transitionHistory.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Stage History
              </h3>
              <div className="space-y-2">
                {(application.transitionHistory as StageTransition[]).map((transition, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400 py-1.5 border-b border-gray-100 dark:border-gray-700 last:border-0"
                  >
                    <span className="font-mono bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">
                      {transition.from || 'initial'}
                    </span>
                    <ArrowRight className="h-3 w-3 flex-shrink-0 text-gray-400" />
                    <span className="font-mono bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">
                      {transition.to}
                    </span>
                    <span className="ml-auto text-gray-400 dark:text-gray-500 whitespace-nowrap">
                      {new Date(transition.timestamp).toLocaleDateString()}
                    </span>
                    {transition.reason && (
                      <span className="text-amber-600 dark:text-amber-400 italic">
                        ({transition.reason})
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Feedback & Review Versions */}
          <div ref={feedbackRef}>
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setIsFeedbackSectionOpen(!isFeedbackSectionOpen)}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <MessageSquare className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    Feedback & Notes
                  </h3>
                </div>
                {isFeedbackSectionOpen ? (
                  <ChevronUp className="h-4 w-4 text-gray-400" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                )}
              </button>
              {isFeedbackSectionOpen && (
                <div className="border-t border-gray-100 dark:border-gray-700 p-4">
                  <FeedbackPanel
                    applicationId={applicationId}
                    forceShowForm={feedbackFormOpen}
                    onFormToggle={setFeedbackFormOpen}
                  />
                </div>
              )}
            </div>
          </div>
          {/* Interviewer Notes (US-139) — HireLoop messages loaded on-demand */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <button
              onClick={handleLoadNotes}
              className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              disabled={notesLoading}
            >
              <div className="flex items-center gap-2">
                <MessageCircle className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Interviewer Notes
                </h3>
              </div>
              {notesLoading ? (
                <RefreshCw className="h-4 w-4 text-gray-400 animate-spin" />
              ) : notesLoaded ? (
                isNotesExpanded ? (
                  <ChevronUp className="h-4 w-4 text-gray-400" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                )
              ) : (
                <ChevronRight className="h-4 w-4 text-gray-400" />
              )}
            </button>

            {isNotesExpanded && notesData && (
              <div className="border-t border-gray-100 dark:border-gray-700 p-4 space-y-3">
                {notesData.scopeError && (
                  <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                    <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-700 dark:text-amber-400">
                      Note: SR OAuth scope &apos;messages.read&apos; may not be enabled. Contact your SR administrator.
                    </p>
                  </div>
                )}
                {notesData.messages.length === 0 && !notesData.scopeError ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-2">
                    No interviewer notes found
                  </p>
                ) : (
                  notesData.messages.map((msg) => (
                    <div
                      key={msg.id}
                      className="pb-3 border-b border-gray-100 dark:border-gray-700 last:border-0 last:pb-0"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                          {msg.authorName}
                        </span>
                        {msg.createdAt && (
                          <span className="text-xs text-gray-400 dark:text-gray-500">
                            {formatDistanceToNow(new Date(msg.createdAt), { addSuffix: true })}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">
                        {msg.body}
                      </p>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Chat Drawer */}
      <ChatDrawer
        applicationId={applicationId}
        candidateName={
          application.candidate
            ? `${application.candidate.firstName} ${application.candidate.lastName}`
            : 'Candidate'
        }
        isOpen={isChatOpen}
        onClose={() => setIsChatOpen(false)}
      />
    </div>
  );
}
